import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/contexts/AuthContext'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { OAuthButtons } from '@/components/OAuthButtons'
import { apiErrorBody, apiErrorDetail } from '@/lib/api-client'
import toast from 'react-hot-toast'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  mfaToken: z.string().optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [_userId, setUserId] = useState<string | null>(null)
  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // After login, continue any pending OAuth flow. nu-core's /oauth2/authorize
  // redirects unauthenticated users here with ?return_to=<authorize URL>. Because
  // authorize is Bearer-authenticated and cross-origin, we cannot simply redirect
  // the browser to it (a top-level nav can't send the token, and a cross-origin
  // 302 is opaque). Instead we complete it with an XHR in JSON mode and send the
  // browser to the client callback it returns (e.g. gui.nuccs.co). Guarded
  // against open redirects: only same-origin / *.nugui.co / *.nuccs.co hosts.
  const isTrustedHost = (host: string) =>
    host === 'nugui.co' || host.endsWith('.nugui.co') ||
    host === 'nuccs.co' || host.endsWith('.nuccs.co')

  const redirectAfterLogin = async () => {
    const returnTo = searchParams.get('return_to')
    if (returnTo) {
      try {
        const url = new URL(returnTo, window.location.origin)
        const trusted = url.origin === window.location.origin || isTrustedHost(url.hostname)
        if (trusted) {
          const token = localStorage.getItem('accessToken')
          if (token && url.pathname.includes('/oauth2/authorize')) {
            const resp = await fetch(url.toString(), {
              headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            })
            if (resp.ok) {
              const data = await resp.json().catch(() => null)
              if (data?.redirect) { window.location.href = data.redirect; return }
            }
          }
          // Non-authorize trusted return_to → navigate directly.
          window.location.href = url.toString()
          return
        }
      } catch {
        /* malformed return_to — fall through to the dashboard */
      }
    }
    navigate('/dashboard')
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true)
    try {
      const result = await login(data.email, data.password, data.mfaToken)
      
      if (result.requiresMfa) {
        setMfaRequired(true)
        setUserId(result.userId || null)
        toast.success('Please enter your MFA code')
      } else {
        await redirectAfterLogin()
      }
    } catch (error: unknown) {
      const body = apiErrorBody(error)
      if (body?.type === 'https://http.dev/problems/mfa-required') {
        setMfaRequired(true)
        setUserId(body.userId ?? null)
      } else {
        setError('root', {
          message: apiErrorDetail(error, 'Login failed'),
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/auth/register" className="font-medium text-primary-600 hover:text-primary-500">
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 rounded-md">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {mfaRequired && (
              <div>
                <label htmlFor="mfaToken" className="block text-sm font-medium text-gray-700">
                  MFA Code
                </label>
                <input
                  {...register('mfaToken')}
                  type="text"
                  autoComplete="one-time-code"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
                  placeholder="000000"
                  maxLength={6}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Enter the 6-digit code from your authenticator app
                </p>
                {errors.mfaToken && (
                  <p className="mt-1 text-sm text-red-600">{errors.mfaToken.message}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link to="/auth/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                Forgot your password?
              </Link>
            </div>
          </div>

          {errors.root && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{errors.root.message}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <OAuthButtons />
        </form>
      </div>
    </div>
  )
}